import { RateUnitEnum } from "@platform/openapi-types";

import { isVolumeBasedRate } from "./premium-calculations";
import { coverageTierToLabelLong } from "./rateFormatters";

import type { Rate, RateSchedule } from "../types";

/** The numeric values an analyst can edit on a rate row. */
export const RATE_CELL_FIELDS = ["rate", "volume", "numberOfLives"] as const;

export type RateCellField = (typeof RATE_CELL_FIELDS)[number];

/** Marks a field path as addressing the rates area rather than a plan benefit. */
export const RATE_FIELD_PATH_PREFIX = "rates";

const SEPARATOR = "::";

/**
 * PEPM schedules carry several rates that share every other coordinate, so the
 * unit is the only thing telling their rows apart. Every other design keeps one
 * unit across the whole schedule, where including it would only invalidate the
 * path when an analyst changes the unit.
 */
const PEPM_UNITS = new Set<string>([
  RateUnitEnum.fmlaPepm,
  RateUnitEnum.adaPepm,
  RateUnitEnum.totalPepm,
]);

/**
 * Identifies a schedule among its plan's schedules in the same category.
 *
 * Position is the schedule's own ordering key, so it is unique among siblings —
 * a carrier that splits AD&D across several schedules still gets distinct keys.
 * The coverage indicator rides along to keep a life plan's two schedules legible.
 */
const scheduleKey = (schedule: RateSchedule): string =>
  `${schedule.rateCoverageIndicator ?? "base"}:${schedule.position}`;

/**
 * Identifies a rate row within its schedule by the coordinates the renderers
 * themselves use to pick rates apart: tier, age band, gender, tobacco, and
 * PEPM unit. A row carrying none of them is the schedule's only row, so its
 * position identifies it.
 */
const rowKey = (rate: Rate): string => {
  const parts: string[] = [];

  if (rate.coverageTierCode) {
    parts.push(`tier:${rate.coverageTierCode}`);
  }

  if (rate.ageBandLower != null || rate.ageBandUpper != null) {
    parts.push(`band:${rate.ageBandLower ?? ""}-${rate.ageBandUpper ?? ""}`);
  }

  if (rate.gender) {
    parts.push(`gender:${rate.gender}`);
  }

  if (rate.isTobaccoRated != null) {
    parts.push(`tobacco:${rate.isTobaccoRated}`);
  }

  if (PEPM_UNITS.has(rate.unit)) {
    parts.push(`unit:${rate.unit}`);
  }

  return parts.length > 0 ? parts.join(":") : `pos:${rate.position}`;
};

/**
 * Builds the stable identifier a rate cell's review is stored against.
 *
 * Saving a structure change from the RatesDrawer deletes and recreates a plan's
 * rates, so rate ids cannot key a review that has to outlive the edit. These
 * coordinates do: a tier or age band that still exists keeps its review, and one
 * the analyst removed drops it.
 */
export const buildRateFieldPath = (
  schedule: RateSchedule,
  rate: Rate,
  field: RateCellField,
): string =>
  [
    RATE_FIELD_PATH_PREFIX,
    schedule.rateCategory ?? "current",
    scheduleKey(schedule),
    rowKey(rate),
    field,
  ].join(SEPARATOR);

/** Whether a stored field path addresses a rate cell rather than a plan benefit. */
export const isRateFieldPath = (fieldPath: string): boolean =>
  fieldPath.startsWith(`${RATE_FIELD_PATH_PREFIX}${SEPARATOR}`);

/** Builds the path for one cell of a rate row, with the schedule already bound. */
export type RateFieldPathBuilder = (
  rate: Rate | undefined,
  field: RateCellField,
) => string | undefined;

/**
 * Binds a schedule to `buildRateFieldPath` so a renderer can hand its layout
 * sub-components a way to key their cells without threading the schedule through.
 */
export const rateFieldPathBuilder =
  (schedule: RateSchedule | undefined): RateFieldPathBuilder =>
  (rate, field) =>
    rate && schedule ? buildRateFieldPath(schedule, rate, field) : undefined;

/** Which stored field a quantity cell edits, given the schedule's unit style. */
export const quantityFieldOf = (isVolumeBased: boolean): RateCellField =>
  isVolumeBased ? "volume" : "numberOfLives";

/** Which stored field a quantity cell edits, from the row's own unit. */
export const quantityFieldOfRate = (rate: Rate | undefined): RateCellField =>
  quantityFieldOf(isVolumeBasedRate(rate));

/** A row's quantity (volume or enrolled lives, by its own unit) as input text. */
export const quantityValueOf = (rate: Rate): string => {
  const quantity = isVolumeBasedRate(rate) ? rate.volume : rate.numberOfLives;
  return quantity != null ? String(quantity) : "";
};

const FIELD_LABELS: Record<RateCellField, string> = {
  rate: "Rate",
  volume: "Volume",
  numberOfLives: "Enrolled Lives",
};

const PEPM_UNIT_LABELS: Record<string, string> = {
  [RateUnitEnum.fmlaPepm]: "FMLA PEPM",
  [RateUnitEnum.adaPepm]: "ADA PEPM",
  [RateUnitEnum.totalPepm]: "Total PEPM",
};

const CATEGORY_LABELS: Record<string, string> = {
  renewal: "Renewal",
  revisedRenewal: "Revised Renewal",
};

/** Turns one `key:value` coordinate from a row key into something readable. */
const describeRowPart = (key: string, value: string): string | undefined => {
  if (key === "tier") return coverageTierToLabelLong(value);
  if (key === "band") {
    const [lower, upper] = value.split("-");
    return upper ? `Ages ${lower}–${upper}` : `Ages ${lower}+`;
  }
  if (key === "gender") return value === "male" ? "Male" : "Female";
  if (key === "tobacco") return value === "true" ? "Tobacco" : "Non-Tobacco";
  if (key === "unit") return PEPM_UNIT_LABELS[value];
  // `pos` is a positional fallback with nothing to say to a reader.
  return undefined;
};

const describeRow = (rowKey: string): string[] => {
  const parts = rowKey.split(":");
  return parts.flatMap((part, index) =>
    index % 2 === 0
      ? (describeRowPart(part, parts[index + 1] ?? "") ?? [])
      : [],
  );
};

/**
 * A reader-facing label for a rate cell, for surfaces that list reviews outside
 * the grid — the Needs Review comments drawer, and the copy the broker receives.
 * Returns nothing for a path this module did not build.
 */
export const formatRateFieldPath = (fieldPath: string): string | undefined => {
  if (!isRateFieldPath(fieldPath)) {
    return undefined;
  }

  const [, category, scheduleKey, rowKey, field] = fieldPath.split(SEPARATOR);
  if (!field) {
    return undefined;
  }

  const words = [
    CATEGORY_LABELS[category ?? ""],
    scheduleKey?.startsWith("adnd") ? "AD&D" : undefined,
    ...describeRow(rowKey ?? ""),
    FIELD_LABELS[field as RateCellField] ?? field,
  ].filter((word): word is string => Boolean(word));

  return words.join(" ");
};
